import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  IconAlertTriangle,
  IconCheck,
  IconMinus,
  IconRuler,
  IconFileText,
} from "@tabler/icons-react";

import { api } from "../lib/api";
import type { Block, BlockCategory, Division, Estate } from "../lib/types";

interface EstateBalance {
  estate: Estate;
  divisionCount: number;
  blockCount: number;
  titleAreaHa: number | null;
  sumHa: number;
  plantedHa: number;
  landClearingHa: number;
  unplantedHa: number;
  varianceHa: number | null;
  variancePct: number | null;
  status: "NO_TITLE" | "MATCH" | "SLIGHT" | "LARGE";
}

interface LandBalanceProps {
  onCategoryClick?: (cat: BlockCategory | "ALL") => void;
  activeCategory?: BlockCategory | "ALL";
}

export function LandBalance({ onCategoryClick, activeCategory }: LandBalanceProps) {
  const estatesQ = useQuery({
    queryKey: ["estates"],
    queryFn: () => api.list<Estate>("/v1/estates"),
  });
  const divisionsQ = useQuery({
    queryKey: ["divisions"],
    queryFn: () => api.list<Division>("/v1/divisions"),
  });
  const blocksQ = useQuery({
    queryKey: ["blocks"],
    queryFn: () => api.list<Block>("/v1/blocks?limit=500"),
  });

  const balances = useMemo<EstateBalance[]>(() => {
    const estates = estatesQ.data?.data ?? [];
    const divisions = divisionsQ.data?.data ?? [];
    const blocks = blocksQ.data?.data ?? [];

    return estates.map((estate) => {
      const estateDivIds = divisions.filter((d) => d.estateId === estate.id).map((d) => d.id);
      const estateBlocks = blocks.filter((b) => estateDivIds.includes(b.divisionId));

      const sumHa = estateBlocks.reduce((s, b) => s + Number(b.hectaresTotal ?? 0), 0);
      const plantedHa = estateBlocks
        .filter((b) => b.category === "PLANTED")
        .reduce((s, b) => s + Number(b.hectaresTotal ?? 0), 0);
      const landClearingHa = estateBlocks
        .filter((b) => b.category === "LAND_CLEARING")
        .reduce((s, b) => s + Number(b.hectaresTotal ?? 0), 0);
      const unplantedHa = estateBlocks
        .filter((b) => b.category === "UNPLANTED")
        .reduce((s, b) => s + Number(b.hectaresTotal ?? 0), 0);

      const titleAreaHa = estate.titleAreaHa ? Number(estate.titleAreaHa) : null;
      let varianceHa: number | null = null;
      let variancePct: number | null = null;
      let status: EstateBalance["status"] = "NO_TITLE";
      if (titleAreaHa != null && titleAreaHa > 0) {
        varianceHa = sumHa - titleAreaHa;
        variancePct = (varianceHa / titleAreaHa) * 100;
        const absPct = Math.abs(variancePct);
        status = absPct <= 5 ? "MATCH" : absPct <= 10 ? "SLIGHT" : "LARGE";
      }

      return {
        estate,
        divisionCount: estateDivIds.length,
        blockCount: estateBlocks.length,
        titleAreaHa,
        sumHa,
        plantedHa,
        landClearingHa,
        unplantedHa,
        varianceHa,
        variancePct,
        status,
      };
    });
  }, [estatesQ.data, divisionsQ.data, blocksQ.data]);

  if (estatesQ.isLoading) {
    return <div className="text-xs text-text-secondary p-3">Loading land balance…</div>;
  }
  if (!balances.length) return null;

  return (
    <div className="mb-4">
      <div className="text-[11px] text-text-secondary uppercase tracking-wide mb-1.5 flex items-center gap-1">
        <IconRuler size={13} /> Land balance · title vs sum-of-blocks
      </div>
      <div className="space-y-2">
        {balances.map((b) => (
          <EstateBalanceCard
            key={b.estate.id}
            balance={b}
            onCategoryClick={onCategoryClick}
            activeCategory={activeCategory}
          />
        ))}
      </div>
    </div>
  );
}

function EstateBalanceCard({
  balance,
  onCategoryClick,
  activeCategory,
}: {
  balance: EstateBalance;
  onCategoryClick?: (cat: BlockCategory | "ALL") => void;
  activeCategory?: BlockCategory | "ALL";
}) {
  const {
    estate,
    divisionCount,
    blockCount,
    titleAreaHa,
    sumHa,
    plantedHa,
    landClearingHa,
    unplantedHa,
    varianceHa,
    variancePct,
    status,
  } = balance;

  const statusColor =
    status === "MATCH"
      ? "bg-bg-success text-text-success border-border-success"
      : status === "SLIGHT"
        ? "bg-bg-warning text-text-warning border-border-warning"
        : status === "LARGE"
          ? "bg-bg-danger text-text-danger border-border-danger"
          : "bg-surface-1 text-text-secondary border-border";

  const StatusIcon =
    status === "MATCH"
      ? IconCheck
      : status === "SLIGHT"
        ? IconMinus
        : status === "LARGE"
          ? IconAlertTriangle
          : IconFileText;

  return (
    <div className="bg-surface-2 border border-border rounded overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 bg-surface-1 border-b border-border">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs bg-surface-2 border border-border px-1.5 py-0.5 rounded">
            {estate.code}
          </span>
          <span className="text-sm font-medium">{estate.name}</span>
          {estate.titleNumber && (
            <span className="text-[10.5px] text-text-secondary flex items-center gap-0.5">
              <IconFileText size={11} /> {estate.titleNumber}
            </span>
          )}
        </div>
        <span
          className={`px-2 py-0.5 rounded text-[11px] font-medium border flex items-center gap-1 ${statusColor}`}
        >
          <StatusIcon size={11} />
          {status === "NO_TITLE"
            ? "No title area set"
            : status === "MATCH"
              ? "Match"
              : status === "SLIGHT"
                ? `Slight ${variancePct != null ? formatSigned(variancePct, 1) + "%" : ""}`
                : `Large ${variancePct != null ? formatSigned(variancePct, 1) + "%" : ""}`}
        </span>
      </div>

      <div className="grid grid-cols-6 gap-0 text-xs">
        <BalanceCell
          label="Title area"
          value={titleAreaHa != null ? titleAreaHa.toFixed(2) + " ha" : "— set on estate"}
          className={titleAreaHa == null ? "text-text-muted" : ""}
        />
        <BalanceCell
          label="Sum of blocks"
          value={sumHa.toFixed(2) + " ha"}
          hint={`${blockCount} blocks · ${divisionCount} div`}
          onClick={onCategoryClick ? () => onCategoryClick("ALL") : undefined}
          active={activeCategory === "ALL"}
          hoverHint="Show all blocks"
        />
        <BalanceCell
          label="Planted"
          value={plantedHa.toFixed(2)}
          className="text-text-success"
          onClick={onCategoryClick ? () => onCategoryClick("PLANTED") : undefined}
          active={activeCategory === "PLANTED"}
          hoverHint="Filter to Planted"
        />
        <BalanceCell
          label="Land clearing"
          value={landClearingHa.toFixed(2)}
          className="text-text-warning"
          onClick={onCategoryClick ? () => onCategoryClick("LAND_CLEARING") : undefined}
          active={activeCategory === "LAND_CLEARING"}
          hoverHint="Filter to Land clearing"
        />
        <BalanceCell
          label="Unplanted"
          value={unplantedHa.toFixed(2)}
          className="text-text-secondary"
          onClick={onCategoryClick ? () => onCategoryClick("UNPLANTED") : undefined}
          active={activeCategory === "UNPLANTED"}
          hoverHint="Filter to Unplanted"
        />
        <BalanceCell
          label="Variance"
          value={varianceHa != null ? formatSigned(varianceHa, 2) + " ha" : "—"}
          className={
            status === "LARGE"
              ? "text-text-danger font-medium"
              : status === "SLIGHT"
                ? "text-text-warning"
                : status === "MATCH"
                  ? "text-text-success"
                  : "text-text-muted"
          }
        />
      </div>

      {status !== "MATCH" && status !== "NO_TITLE" && (
        <div className="px-3 py-2 bg-surface-1 border-t border-border text-[11px] text-text-secondary leading-relaxed">
          {(varianceHa ?? 0) > 0 ? (
            <>
              <strong>Sum &gt; Title</strong> — blocks total more than the land title. Likely
              causes: duplicate block entered, overlapping polygons, or title area
              under-recorded. Cross-check with the original title deed.
            </>
          ) : (
            <>
              <strong>Sum &lt; Title</strong> — blocks total less than the land title. Likely
              causes: missing block, HCV/road/drainage not yet entered as an Unplanted block, or
              title area over-recorded. Compare against the estate map.
            </>
          )}
        </div>
      )}
      {status === "NO_TITLE" && (
        <div className="px-3 py-2 bg-surface-1 border-t border-border text-[11px] text-text-muted leading-relaxed">
          Edit this estate in the Hierarchy tab (or via API) to set{" "}
          <code>title_area_ha</code> and activate the balance check.
        </div>
      )}
    </div>
  );
}

function BalanceCell({
  label,
  value,
  hint,
  className = "",
  onClick,
  active,
  hoverHint,
}: {
  label: string;
  value: string;
  hint?: string;
  className?: string;
  onClick?: () => void;
  active?: boolean;
  hoverHint?: string;
}) {
  const clickable = Boolean(onClick);
  const activeStyle = active ? "bg-surface-1" : "";
  return (
    <div
      onClick={onClick}
      title={hoverHint}
      className={`px-3 py-2 border-r border-border last:border-r-0 ${activeStyle} ${
        clickable ? "cursor-pointer hover:bg-surface-1 group" : ""
      }`}
    >
      <div className="text-[10px] text-text-secondary uppercase tracking-wide flex items-center justify-between">
        <span>{label}</span>
        {clickable && (
          <span className="opacity-0 group-hover:opacity-100 transition-opacity text-text-accent text-[9px]">
            filter →
          </span>
        )}
      </div>
      <div className={`font-medium ${className}`}>{value}</div>
      {hint && <div className="text-[10px] text-text-muted">{hint}</div>}
    </div>
  );
}

function formatSigned(n: number, decimals: number): string {
  return (n > 0 ? "+" : "") + n.toFixed(decimals);
}
